import { Component, Element, Event, EventEmitter, h, Host, Method, Prop, State, Watch } from "@stencil/core";
import { getUniqueId } from "../../utils/unique-id";
import { hasSlot } from "../../utils/hasSlot";

/**
 * @slot (default) - Content of the label.
 * @slot "hint" - Content of the hint block.
 * @slot "buttons" - Buttons can be placed here.
 * @slot "other" - Additional content is placed at the bottom of the component.
 */
@Component({
    tag: "ux-input-field",
    styleUrl: "input-field.scss",
    shadow: false
})
export class InputField {
    private inputElement: HTMLInputElement;
    @Element() domNode!: HTMLUxInputFieldElement;

    /**
     * Label text
     */
    @Prop() readonly label: string = "";
    /**
     * Value of the input element
     */
    @Prop({ mutable: true }) value = "";
    /**
     * The id attribute of the input element
     */
    @Prop({ mutable: true }) inputId = "";
    /**
     * The type attribute of the input element
     */
    @Prop() readonly type: "email" | "number" | "password" | "search" | "text" | "url" = "text";
    /**
     * The name attribute of the input element
     */
    @Prop() readonly name?: string;
    /**
     * Additional info label for the input field
     */
    @Prop() readonly labelInfo: string = "";
    /**
     * Specifies the maximum number of characters allowed in the input field
     */
    @Prop() readonly maxLength?: number;
    /**
     * Placeholder for the input element
     */
    @Prop() readonly placeholder: string = "";
    /**
     * Indicates if the input is disabled
     */
    @Prop() readonly disabled: boolean = false;
    /**
     * Indicates if the input is required
     */
    @Prop() readonly required: boolean = false;
    /**
     * Indicates if the input value is invalid
     */
    @Prop({ mutable: true }) error = false;
    /**
     * Optional error message that is rendered if `error` is set to `true`
     */
    @Prop() readonly errorMsg: string = "";
    /**
     * Name of the icon that should be displayed in error state
     */
    @Prop() readonly errorIcon: string = "";
    /**
     * Additional hint text shown below the input
     */
    @Prop() readonly hintText: string = "";
    /**
     * If set to `true` a clear button is rendered that resets the `value` on click
     */
    @Prop() readonly clearable: boolean = false;
    /**
     * An optional unit is shown beside the input
     */
    @Prop() readonly unit: string | null = null;
    /**
     * Name of the icon that is displayed inside the field
     */
    @Prop() readonly icon: string | null = null;
    /**
     * The inputmode attribute of the input can indicate to the browser which keyboard to display
     */
    // eslint-disable-next-line @stencil-community/reserved-member-names
    @Prop() readonly inputmode: "none" | "text" | "decimal" | "numeric" | "tel" | "search" | "email" | "url";
    /**
     * Autocomplete attribute, will be set on the input element. Refer to HTML standards for allowed values
     */
    @Prop() readonly autocomplete: string = "off";
    /**
     * If set, the input field will present a copy to clipboard functionality
     */
    @Prop() readonly copyToClipboard: boolean = false;

    /**
     * Indicates if the component has focus or not
     */
    @State() hasFocus = false;

    /**
     * Set inputId if it is unset
     */
    @Watch("inputId")
    setInputId() {
        this.inputId = this.inputId || getUniqueId();
    }

    /**
     * Returns the native input element
     */
    @Method()
    async getInputElement(): Promise<HTMLInputElement> {
        return this.inputElement;
    }

    /**
     * Set the focus on the input element
     */
    @Method()
    async setFocus() {
        this.inputElement.focus();
    }

    /**
     * Emitted when a keyboard input occurs
     */
    @Event() uxInput!: EventEmitter;

    /**
     * Emitted when the value has changed
     */
    @Event() uxChange!: EventEmitter;

    /**
     * Emitted when the input loses focus
     */
    @Event() uxBlur!: EventEmitter<FocusEvent>;

    /**
     * Emitted when the input gains focus
     */
    @Event() uxFocus!: EventEmitter<FocusEvent>;

    /**
     * Emitted when the input is invalid
     */
    @Event() uxInvalid!: EventEmitter;

    componentWillLoad() {
        this.setInputId();
    }

    /**
     * inputChange Event
     */
    @Event({
        eventName: "uxInputChange"
    })
    inputChange!: EventEmitter;

    /*
     * --------------------------------
     * Event handlers
     * --------------------------------
     */

    private handleInput = (e: InputEvent) => {
        this.value = this.inputElement.value;
        this.uxInput.emit();
        this.inputChange.emit({
            currentValue: (e.target as HTMLInputElement).value,
            inputName: this.name,
            inputId: this.inputId
        });
    };

    private handleChange = () => {
        this.value = this.inputElement.value;
        this.uxChange.emit();
    };

    private handleFocus = () => {
        this.hasFocus = true;
        this.uxFocus.emit();
    };

    private handleBlur = (event: FocusEvent) => {
        // only emit blur if the whole component loses focus
        if (!this.domNode.contains(event.relatedTarget as HTMLElement)) {
            this.hasFocus = false;
            this.uxBlur.emit();
        }
    };

    private handleInvalid = () => {
        this.error = true;
        this.uxInvalid.emit();
    };

    private handleClear = (event: Event) => {
        event.stopPropagation();
        event.preventDefault();
        this.setFocus();
        this.value = "";
        this.uxChange.emit();
    };

    render() {
        const {
            label,
            labelInfo,
            maxLength,
            value,
            inputId,
            type,
            name,
            placeholder,
            disabled,
            required,
            error,
            errorMsg,
            errorIcon,
            hintText,
            inputmode,
            clearable,
            unit,
            handleBlur,
            handleChange,
            handleClear,
            handleFocus,
            handleInput,
            handleInvalid,
            hasFocus,
            autocomplete,
            copyToClipboard
        } = this;

        const baseClass = "ux-input-field";
        const hasValue = value !== "";
        const haslabelSlot = hasSlot(this.domNode, "label");
        const hasIconSlot = hasSlot(this.domNode, "icon");
        const hasHintSlot = hasSlot(this.domNode, "hint");
        const hasButtons = hasSlot(this.domNode, "buttons");
        const hasLabelText = label && label.length > 0;
        const hasLabel = haslabelSlot || hasLabelText;
        const hasUnit = unit !== null;
        const hasUnitText = hasUnit && unit !== "";
        const hasHintText = hintText && hintText.length > 0;
        const hasHint = hasHintText || hasHintSlot;
        const iconName = error && errorIcon !== "" ? errorIcon : this.icon;
        const hasIconName = iconName && iconName.length > 0;
        const hasIcon = hasIconSlot || hasIconName;
        const isClearable = clearable && !disabled && hasValue;
        const labelId = `${baseClass}-label-${inputId}`;
        const hintId = `${baseClass}--hint-${inputId}`;
        return (
            <Host
                class={{
                    [baseClass]: true,
                    [`${baseClass}--disabled`]: disabled,
                    [`${baseClass}--clearable`]: isClearable,
                    [`${baseClass}--numeric`]: hasUnit,
                    [`${baseClass}--unit`]: hasUnitText,
                    [`${baseClass}--error`]: error,
                    [`${baseClass}--focus`]: hasFocus
                }}
            >
                <ux-input-label
                    text={label}
                    labelId={labelId}
                    labelClasses={`${baseClass}__label`}
                    inputId={inputId}
                    error={error}
                    errorMsg={errorMsg}
                    required={required}
                    labelInfo={labelInfo}
                >
                    <slot />
                </ux-input-label>
                <div class={`${baseClass}__field ux-u-background-white`}>
                    <div class={`${baseClass}__controls`}>
                        <input
                            class={{
                                [`ux-input`]: true,
                                [`${baseClass}__input`]: true
                            }}
                            type={type}
                            id={inputId}
                            name={name}
                            value={value}
                            placeholder={placeholder}
                            disabled={disabled}
                            required={required}
                            maxlength={maxLength}
                            spellcheck={false}
                            inputMode={inputmode}
                            aria-labelledby={hasLabel ? labelId : null}
                            aria-describedby={hasHint ? hintId : null}
                            // @ts-expect-error Check for typing
                            ref={(el) => (this.inputElement = el)}
                            onInput={handleInput}
                            onChange={handleChange}
                            onFocus={handleFocus}
                            onBlur={handleBlur}
                            onInvalid={handleInvalid}
                            aria-invalid={error ? "true" : "false"}
                            autocomplete={autocomplete}
                        />
                        {isClearable && (
                            <button type="button" class={`${baseClass}__clear`} onClick={handleClear} tabIndex={-1}>
                                <ux-icon name="cross" weight="normal" />
                            </button>
                        )}
                        {hasUnitText && (
                            <div class={`${baseClass}__unit`} onClick={() => this.setFocus()}>
                                {unit}
                            </div>
                        )}
                        {hasIcon && (
                            <div class={`${baseClass}__icon`}>
                                {/* @ts-expect-error Check for typing */}
                                <ux-icon name={iconName} />
                            </div>
                        )}
                        {copyToClipboard && (
                            <div class={`${baseClass}__icon`}>
                                <ux-copy-to-clipboard
                                    hide-text-to-copy
                                    text-to-copy={this.value || ""}
                                    click-to-copy-icon="copy"
                                ></ux-copy-to-clipboard>
                            </div>
                        )}
                    </div>
                    <div
                        class={{
                            [`${baseClass}__buttons`]: hasButtons,
                            [`${baseClass}--hidden`]: !hasButtons
                        }}
                    >
                        <slot name="buttons" />
                    </div>
                </div>
                <div
                    id={hintId}
                    class={{
                        [`${baseClass}__hint`]: hasHint,
                        [`${baseClass}--hidden`]: !hasHint
                    }}
                >
                    {hasHintText && <div class={`${baseClass}__hint-text`}>{hintText}</div>}
                    <div
                        class={{
                            [`${baseClass}__hint-slot`]: hasHintSlot,
                            [`${baseClass}--hidden`]: !hasHintSlot
                        }}
                    >
                        <slot name="hint" />
                    </div>
                </div>
                <slot name="other" />
            </Host>
        );
    }
}
